/**
 * sanitize.ts —— AI 输出清洗
 *
 * 为什么需要这一层：
 *   我们要求模型只输出裸 IL 代码（D7）。但实测下来，模型总会忍不住加东西：
 *     · Markdown 代码围栏 ```il ... ```
 *     · 「好的，我来帮你切换：」这类前言
 *     · 「（已将主题切换为深色）」这类后记 —— 这是最危险的，它违反了信任原则
 *     · 注释 # ...
 *
 * 所以清洗不是「防呆」，是信任原则的技术保障之一：
 *   把不属于指令的部分剥掉，让执行器只看到干净的命令。
 *
 * ⚠️ 本文件**不做**任何语义改写。它只做「剥壳」：
 *   如果模型输出的确实是命令，剥完还是命令；
 *   如果不是命令，剥完也不是 —— 后面的解析器会报错，走失败回传。
 *   绝不试图把自然语言「翻译」成命令，那是模型的职责，越俎代庖会掩盖问题。
 */

/** 清洗结果 */
export interface SanitizeResult {
  /** 清洗后的 IL 源码（可能为空字符串） */
  code: string;
  /** 被剥掉的原文，仅用于调试展示 */
  removed: string[];
}

/**
 * 完整的清洗流程。
 *
 * 顺序有讲究：
 *   ① 先剥围栏 —— 否则围栏内的内容会被当注释处理掉
 *   ② 再去整行注释
 *   ③ 最后裁剪首尾空白
 */
export function sanitize(raw: string): SanitizeResult {
  const removed: string[] = [];
  let text = raw ?? '';

  // ── ① 提取代码围栏内容 ──
  //
  // 不锚定行首：模型常写成「好的，我来帮你：\n```il\n...\n```」，
  // 前言在围栏之前。所以策略是「找到第一对围栏，只取围栏内的内容」，
  // 外面的文字（无论前言后记）全部丢弃。
  const fenceRe = /(?:```|~~~)[^\n]*\n([\s\S]*?)\n?\s*(?:```|~~~)/;
  const fenced = text.match(fenceRe);
  if (fenced) {
    removed.push('代码围栏' + (text.slice(0, fenced.index).trim() ? ' + 围栏外文字' : ''));
    text = fenced[1];
  } else {
    // 没有成对围栏，但可能有个孤零零的开头/结尾标记
    const looseOpen = text.match(/^\s*(?:```|~~~)[^\n]*\n([\s\S]*)$/);
    if (looseOpen) {
      removed.push('未闭合的代码围栏');
      text = looseOpen[1];
    }
    const looseClose = text.match(/^([\s\S]*?)\n?\s*(?:```|~~~)\s*$/);
    if (looseClose) {
      removed.push('孤立的收尾围栏');
      text = looseClose[1];
    }
  }

  // ── ② 逐行处理 ──
  const lines = text.split(/\r?\n/);
  const kept: string[] = [];

  for (const line of lines) {
    const trimmed = line.trim();

    // 空行直接跳过（IL 用 ; 分隔，换行没有语义）
    if (!trimmed) continue;

    // 整行注释。除了我们的 `#`，也吃掉 `//` —— 模型常把语言标成 js/ts
    // 然后按 JS 习惯写注释，既然围栏内容都收下了，就一并清理
    if (trimmed.startsWith('#') || trimmed.startsWith('//')) {
      removed.push(trimmed);
      continue;
    }

    // 去掉行尾注释（# 和 //，但只切引号外的部分）
    kept.push(stripTrailingComment(line).trim());
  }

  // ── ③ 收尾 ──
  text = kept.filter(Boolean).join('\n').trim();

  // ── ④ 没括号就没指令 ──
  //
  // IL 的形式是 `a.b(args)`，一定含括号。模型偶尔会无视规则输出
  // 「好的，已经帮你搞定了！」这类纯自然语言。这种内容如果原样送去
  // 解析，解析器会报一个拗口的语法错误；不如在这里就判无效，
  // 让上层走「模型没给指令」的分支，措辞更准确。
  //
  // 注意：这一步只做粗筛，不代表内容是合法 IL —— 合法性仍由解析器判定。
  if (text && !text.includes('(')) {
    removed.push(text);
    text = '';
  }

  // 全空说明模型只吐了废话，没有可执行内容
  if (!text) removed.push('（无有效指令）');

  return { code: text, removed };
}

/**
 * 去掉行尾注释，但不动字符串字面量里的 `#` 或 `//`。
 *
 * 例：
 *   setting.setTheme('dark')  # 切换主题   → setting.setTheme('dark')
 *   explain('井号 # 号')                    → 原样保留
 *   explain('https://a.b')                  → 原样保留（// 在引号内）
 */
function stripTrailingComment(line: string): string {
  let inSingle = false;
  let inDouble = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];

    // 处理转义（只关心字符串内）
    if ((inSingle || inDouble) && ch === '\\') {
      i++; // 跳过被转义的字符
      continue;
    }

    if (ch === "'" && !inDouble) inSingle = !inSingle;
    else if (ch === '"' && !inSingle) inDouble = !inDouble;
    else if (!inSingle && !inDouble) {
      // 引号外的 `#` 开启注释
      if (ch === '#') return line.slice(0, i);
      // 引号外的 `//` 开启注释
      if (ch === '/' && line[i + 1] === '/') return line.slice(0, i);
    }
  }
  return line;
}

/**
 * 判断清洗后的内容像不像一条 IL 指令。
 *
 * 用途：输入栏在处理前做个快速预判，避免把纯聊天内容也丢给解释器。
 * 注意这只是「像不像」的粗筛，真正的合法性由解析器判定 ——
 * 两者不是替代关系，粗筛通过后仍会走完整解析。
 */
export function looksLikeCommand(code: string): boolean {
  if (!code) return false;
  // 至少包含一对括号，且以标识符开头
  return /^[A-Za-z_$][\w$]*(?:\.[A-Za-z_$][\w$]*)*\s*\(/.test(code.trim());
}
